import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import SelectMenu from '../ui/SelectMenu';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';
import { useToast, useConfirm } from '../ui';
import PageHeader from '../ui/PageHeader';
import { PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';

import useTelemetry from '../../hooks/useTelemetry';
import { settingsAPI } from '../../services/api';
import { formatPhoneInput } from '../../utils/phone';

const INITIAL_STATE = {
  company_name: '',
  phone: '',
  email: '',
  address_street: '',
  address_city: '',
  address_state: '',
  address_zip: '',
  website: '',
  offices: [],
};

const OFFICE_LABEL_OPTIONS = [
  { value: 'HQ', label: 'HQ' },
  { value: 'Branch', label: 'Branch' },
  { value: 'Warehouse', label: 'Warehouse' },
];

// Iter18 R2 §2.1: normalize website URLs so users don't have to type https://.
function normalizeWebsite(url) {
  const trimmed = String(url || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export default function CompanyProfilePage() {
  const [form, setForm] = useState(INITIAL_STATE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { emit } = useTelemetry();
  const location = useLocation();
  const toast = useToast();
  const confirm = useConfirm();

  // Iter18 R2 §2.1: draft office editor state. `null` when no editor open;
  // `{ mode: 'add' | 'edit', index?: number, draft: {...} }` otherwise.
  const [officeDraft, setOfficeDraft] = useState(null);

  const fetchCompany = useCallback(async () => {
    try {
      setLoading(true);
      const res = await settingsAPI.getCompanyProfile();
      if (res) {
        setForm({
          company_name: res.company_name || '',
          phone: res.phone || '',
          email: res.email || '',
          address_street: res.address_street || '',
          address_city: res.address_city || '',
          address_state: res.address_state || '',
          address_zip: res.address_zip || '',
          website: res.website || '',
          offices: Array.isArray(res.offices) ? res.offices : [],
        });
      }
    } catch (err) {
      setError('Failed to load company profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCompany(); }, [fetchCompany]);

  // /settings/offices redirects here with hash #offices — scroll on load.
  useEffect(() => {
    if (!loading && location.hash === '#offices') {
      const el = document.getElementById('offices');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, location.hash]);

  const handleChange = (field) => (e) => {
    const value = field === 'phone' ? formatPhoneInput(e.target.value) : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleWebsiteBlur = () => {
    setForm((prev) => ({ ...prev, website: normalizeWebsite(prev.website) }));
  };

  // Iter18 R2 §2.1 + backlog §4.1: Amazon Location Service will drive this globally.
  // TODO(amazon-location): on ZIP blur, call Location Service place index and
  // populate city/state (also address suggest-as-you-type across every address input
  // in the app — customers, quotes, warehouses, calendar, etc.).
  const handleZipBlur = () => { /* no-op stub — replace when Location Service wires up */ };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (officeDraft) {
      setError('Save or cancel the office you are editing before saving the profile.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const payload = { ...form, website: normalizeWebsite(form.website) };
      await settingsAPI.updateCompanyProfile(payload);
      setForm(payload);
      toast.success('Company profile saved');
      emit('settings.company_updated', { fields: Object.keys(payload) });
    } catch (err) {
      setError('Failed to save company profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Office editor lifecycle ───────────────────────────────────────────────
  const startAddOffice = () => {
    setOfficeDraft({
      mode: 'add',
      draft: { label: 'Branch', name: '', address: '', city: '', state: '', zip: '' },
    });
  };

  const startEditOffice = (idx) => {
    setOfficeDraft({
      mode: 'edit',
      index: idx,
      draft: { ...form.offices[idx] },
    });
  };

  const cancelOfficeDraft = () => setOfficeDraft(null);

  const updateDraftField = (field, value) => {
    setOfficeDraft((prev) => (prev ? { ...prev, draft: { ...prev.draft, [field]: value } } : prev));
  };

  // Exactly-one-HQ constraint applied when the draft is saved.
  const saveOfficeDraft = () => {
    if (!officeDraft) return;
    const { mode, index, draft } = officeDraft;

    setForm((prev) => {
      let offices = [...(prev.offices || [])];
      // Downgrade any OTHER office marked HQ if this one is now HQ.
      if (draft.label === 'HQ') {
        offices = offices.map((o, i) => {
          if ((mode === 'edit' && i === index) || o.label !== 'HQ') return o;
          return { ...o, label: 'Branch' };
        });
      }
      if (mode === 'add') offices.push(draft);
      else offices[index] = draft;
      return { ...prev, offices };
    });
    toast.success(mode === 'add' ? 'Office added' : 'Office updated');
    setOfficeDraft(null);
  };

  const removeOffice = async (idx) => {
    const office = form.offices[idx];
    const displayName = office.name || office.address || `Office ${idx + 1}`;
    const ok = await confirm({
      title: 'Remove office?',
      message: `Remove "${displayName}" from your office list?`,
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    setForm((prev) => ({
      ...prev,
      offices: (prev.offices || []).filter((_, i) => i !== idx),
    }));
    // Cancel any open edit that pointed at this row.
    if (officeDraft?.mode === 'edit' && officeDraft.index === idx) {
      setOfficeDraft(null);
    }
    toast.success('Office removed');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="space-y-3 w-full">
          <div className="h-8 rounded animate-pulse" style={{ backgroundColor: 'var(--neutral-100)' }} />
          <div className="h-32 rounded animate-pulse" style={{ backgroundColor: 'var(--neutral-100)' }} />
        </div>
      </div>
    );
  }

  const editorOpen = !!officeDraft;

  return (
    <div className="px-6 space-y-6">
      <PageHeader
        title="Company Profile"
        subtitle="Manage your company information. This appears on quotes and invoices."
      />

      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Profile fields — no per-card save; single Save Changes lives below Office Locations. */}
      <Card>
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(e); }} className="space-y-6 p-6" id="company-profile-form">
          {/* Company Name */}
          <div>
            <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-1">
              Company Name <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <Input
              id="company_name"
              value={form.company_name}
              onChange={handleChange('company_name')}
              placeholder="Your Company Name"
              required
            />
          </div>

          {/* Phone & Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <Input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange('phone')}
                placeholder="(555) 123-4567"
                inputMode="tel"
                autoComplete="tel"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                placeholder="info@company.com"
                autoComplete="email"
              />
            </div>
          </div>

          {/* Business Address — ZIP-first (auto-fill via Amazon Location Service is backlog §4.1) */}
          <fieldset data-address-autocomplete-todo>
            <legend className="block text-sm font-medium text-gray-700 mb-2">Business Address</legend>
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  id="address_zip"
                  value={form.address_zip}
                  onChange={handleChange('address_zip')}
                  onBlur={handleZipBlur}
                  placeholder="ZIP code"
                  aria-label="ZIP code"
                  inputMode="numeric"
                  maxLength={10}
                  autoComplete="postal-code"
                />
                <Input
                  id="address_city"
                  value={form.address_city}
                  onChange={handleChange('address_city')}
                  placeholder="City"
                  aria-label="City"
                  autoComplete="address-level2"
                />
                <Input
                  id="address_state"
                  value={form.address_state}
                  onChange={handleChange('address_state')}
                  placeholder="State"
                  aria-label="State"
                  maxLength={2}
                  autoComplete="address-level1"
                />
              </div>
              <Input
                id="address_street"
                value={form.address_street}
                onChange={handleChange('address_street')}
                placeholder="Street address"
                aria-label="Street address"
                autoComplete="address-line1"
              />
            </div>
            <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
              Enter your ZIP first — City and State will be looked up automatically in a future release.
            </p>
          </fieldset>

          {/* Website — https:// prepended on blur if missing */}
          <div>
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            <Input
              id="website"
              type="text"
              value={form.website}
              onChange={handleChange('website')}
              onBlur={handleWebsiteBlur}
              placeholder="www.company.com"
              autoComplete="url"
            />
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              You can enter this without the <code>https://</code> — we'll add it for you.
            </p>
          </div>
        </form>
      </Card>

      {/* Office Locations */}
      <Card id="offices">
        <div className="px-6 py-4 border-b border-[color:var(--color-border)] flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold" style={{ color: 'var(--color-text-default)' }}>Office Locations</h2>
            <p className="text-xs font-normal mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
              Exactly one office may be marked HQ. Additional locations are Branches or Warehouses.
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={startAddOffice} disabled={editorOpen}>
            <PlusIcon className="h-4 w-4 mr-1.5" />
            Add Office
          </Button>
        </div>
        <div className="divide-y divide-gray-100">
          {(form.offices || []).length === 0 && !editorOpen ? (
            <p className="text-sm px-6 py-8 text-center" style={{ color: 'var(--color-text-muted)' }}>
              No additional offices. Your business address above is used as HQ.
            </p>
          ) : (
            (form.offices || []).map((office, idx) => {
              const isEditingThisRow = officeDraft?.mode === 'edit' && officeDraft.index === idx;
              if (isEditingThisRow) {
                return (
                  <OfficeEditor
                    key={`edit-${idx}`}
                    draft={officeDraft.draft}
                    onChange={updateDraftField}
                    onSave={saveOfficeDraft}
                    onCancel={cancelOfficeDraft}
                    heading="Edit office"
                  />
                );
              }
              return <OfficeRow key={idx} office={office} onEdit={() => startEditOffice(idx)} onDelete={() => removeOffice(idx)} />;
            })
          )}
          {officeDraft?.mode === 'add' && (
            <OfficeEditor
              draft={officeDraft.draft}
              onChange={updateDraftField}
              onSave={saveOfficeDraft}
              onCancel={cancelOfficeDraft}
              heading="Add office"
            />
          )}
        </div>
      </Card>

      {/* Save Changes — Iter18 R2 §2.1: single page-level save, positioned below Office Locations. */}
      <div className="flex justify-end">
        <Button type="submit" form="company-profile-form" disabled={saving || editorOpen}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>
      {editorOpen && (
        <p className="text-xs text-right" style={{ color: 'var(--color-text-muted)' }}>
          Finish adding or editing the office above to enable Save.
        </p>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Office row (persisted / display) & Office editor (draft)
// ────────────────────────────────────────────────────────────────────────────

function OfficeRow({ office, onEdit, onDelete }) {
  const chipStyles =
    office.label === 'HQ'
      ? { backgroundColor: 'var(--navy-50)', color: 'var(--navy-800)' }
      : office.label === 'Warehouse'
      ? { backgroundColor: 'var(--neutral-100)', color: 'var(--color-text-default)' }
      : { backgroundColor: 'var(--neutral-100)', color: 'var(--color-text-muted)' };

  const addressParts = [office.address, office.city, office.state, office.zip].filter(Boolean).join(', ');
  const primaryLabel = office.name || office.label || 'Office';

  return (
    <div className="px-6 py-3 flex items-center gap-3 hover:bg-[color:var(--neutral-50)]">
      <span
        className="inline-flex items-center px-2 py-0.5 text-xs font-semibold flex-shrink-0"
        style={{ ...chipStyles, borderRadius: 'var(--radius-sm)' }}
      >
        {office.label || 'Branch'}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate" style={{ color: 'var(--color-text-default)' }}>
          {primaryLabel}
        </p>
        {addressParts && (
          <p className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>
            {addressParts}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="p-1.5 rounded hover:bg-[color:var(--neutral-100)]"
        aria-label={`Edit ${primaryLabel}`}
        title="Edit"
      >
        <PencilSquareIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="p-1.5 rounded hover:bg-red-50"
        aria-label={`Delete ${primaryLabel}`}
        title="Delete"
      >
        <TrashIcon className="h-4 w-4" style={{ color: 'var(--color-error)' }} />
      </button>
    </div>
  );
}

function OfficeEditor({ draft, onChange, onSave, onCancel, heading }) {
  return (
    <div
      className="px-6 py-4 space-y-3"
      style={{ backgroundColor: 'var(--color-surface-sunken)' }}
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold" style={{ color: 'var(--color-text-default)' }}>{heading}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-muted)' }}>Label</label>
          <SelectMenu
            value={draft.label || 'Branch'}
            onChange={(val) => onChange('label', val)}
            options={OFFICE_LABEL_OPTIONS}
            width="full"
          />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-muted)' }}>Office name</label>
          <Input
            value={draft.name || ''}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder="e.g. Downtown Office"
          />
        </div>
      </div>
      <div data-address-autocomplete-todo>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Input
            placeholder="ZIP"
            value={draft.zip || ''}
            onChange={(e) => onChange('zip', e.target.value)}
            inputMode="numeric"
            maxLength={10}
            autoComplete="postal-code"
          />
          <Input
            placeholder="City"
            value={draft.city || ''}
            onChange={(e) => onChange('city', e.target.value)}
            autoComplete="address-level2"
          />
          <Input
            placeholder="State"
            value={draft.state || ''}
            onChange={(e) => onChange('state', e.target.value)}
            maxLength={2}
            autoComplete="address-level1"
          />
        </div>
        <Input
          placeholder="Street address"
          value={draft.address || ''}
          onChange={(e) => onChange('address', e.target.value)}
          className="mt-2"
          autoComplete="address-line1"
        />
      </div>
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={onSave}>Save Office</Button>
      </div>
    </div>
  );
}
